import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Clock, Copy, ExternalLink, Trash2, X } from "lucide-react";
import { API } from "../../App";
import { Button } from "../ui/button";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Europe/Madrid" }) : "–");
const STATUS = { held: "bg-amber-100 text-amber-800", paid: "bg-emerald-100 text-emerald-800", expired: "bg-stone-100 text-stone-500", released: "bg-stone-100 text-stone-500" };

// Admin: Founder Edition seats, the bank transfer that holds them, the pledges and the LinkedIn line.
export default function FounderEditionCard({ headers }) {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/admin/sojourns/founder`, { headers }).then((r) => { setData(r.data); setCfg(r.data.config); }).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/sojourns/founder/config`, cfg, { headers });
      toast.success("Founder Edition settings saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const act = async (id, action, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    try {
      if (action === "delete") await axios.delete(`${API}/admin/sojourns/founder/${id}`, { headers });
      else await axios.post(`${API}/admin/sojourns/founder/${id}/${action}`, {}, { headers });
      toast.success({ confirm: "Seat confirmed, email sent", release: "Seat released", extend: "Hold extended", delete: "Pledge deleted" }[action]);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't work");
    }
  };

  const copy = async (text) => { try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Couldn't copy"); } };

  if (!data || !cfg) return null;
  const { state, pledges, linkedin, card_url } = data;
  const set = (k) => (e) => setCfg((c) => ({ ...c, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 mb-6" data-testid="admin-founder-edition">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-medium text-[#2E2A26]">Founder Edition</h2>
          <p className="text-xs text-[#3D4035]/60 mt-1 max-w-xl">Seats sold before anything is known. A pledge holds a seat for {cfg.hold_hours} hours while the bank transfer lands; confirm it here when the money arrives and the buyer gets the "seat is yours" email. The word "deposit" isn't used anywhere the buyer sees.</p>
        </div>
        <div className="text-right" data-testid="admin-founder-summary">
          <p className="text-2xl font-light text-[#2E2A26]">{state.taken + state.held} <span className="text-sm text-[#3D4035]/50">of {state.seats}</span></p>
          <p className="text-[11px] text-[#3D4035]/60">{state.taken} paid · {state.held} held · {state.available} open{state.sold_out ? " · SOLD OUT" : ""}</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3 mt-5">
        {[["seats", "Founder seats", 0, 12], ["deposit", "Amount to transfer (€)", 0, 100000], ["discount", "Founder fare, % off", 0, 90], ["hold_hours", "Hold (hours)", 1, 720]].map(([k, label, min, max]) => (
          <label key={k} className="text-xs text-[#3D4035]/60">
            {label}
            <input type="number" min={min} max={max} value={cfg[k]} onChange={set(k)} className="mt-1 h-10 w-full px-3 rounded-lg border border-stone-200 text-sm text-[#2E2A26] bg-white" data-testid={`admin-founder-${k}`} />
          </label>
        ))}
      </div>
      <label className="block text-xs text-[#3D4035]/60 mt-3">
        Bank details for the hold email (account name, IBAN, BIC). Empty = the email says you'll send them personally.
        <textarea rows={3} value={cfg.bank} onChange={set("bank")} className="mt-1 w-full px-3 py-2 rounded-lg border border-stone-200 text-sm text-[#2E2A26] bg-white font-mono" placeholder={"Savi Coliving SL\nIBAN ES00 0000 0000 0000 0000 0000\nBIC XXXXESMM"} data-testid="admin-founder-bank" />
      </label>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
        <label className="flex items-center gap-2 text-sm text-[#2E2A26]">
          <input type="checkbox" checked={cfg.enabled} onChange={set("enabled")} data-testid="admin-founder-enabled" /> Shown on the Sojourns page
        </label>
        <Button onClick={save} disabled={saving} className="bg-[#2E2A26] hover:bg-black text-white rounded-full" data-testid="admin-founder-save">{saving ? "Saving" : "Save settings"}</Button>
      </div>

      <div className="mt-6 pt-5 border-t border-stone-100">
        <h3 className="text-sm font-medium text-[#2E2A26] mb-2">Pledges</h3>
        {pledges.length === 0 ? <p className="text-xs text-[#3D4035]/50" data-testid="admin-founder-empty">No founder pledges yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="admin-founder-table">
              <thead className="text-left text-[#3D4035]/50 uppercase tracking-wider">
                <tr><th className="py-1.5 pr-3">Seat</th><th className="py-1.5 pr-3">Who</th><th className="py-1.5 pr-3">Reference</th><th className="py-1.5 pr-3">Status</th><th className="py-1.5 pr-3">Held until</th><th className="py-1.5">Actions</th></tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {pledges.map((p) => (
                  <tr key={p.id} data-testid={`admin-founder-row-${p.seat}`} data-status={p.status}>
                    <td className="py-2 pr-3 font-mono text-[#C85A38]">{p.seat}</td>
                    <td className="py-2 pr-3">
                      <div className="text-[#2E2A26]">{p.name}</div>
                      <div className="text-[#3D4035]/60">{p.email}{p.phone ? ` · ${p.phone}` : ""}</div>
                      {p.business && <div className="text-[#3D4035]/50">{p.business}</div>}
                    </td>
                    <td className="py-2 pr-3 font-mono whitespace-nowrap">{p.reference}</td>
                    <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${STATUS[p.status] || ""}`}>{p.status}</span></td>
                    <td className="py-2 pr-3 whitespace-nowrap text-[#3D4035]/70">{p.status === "paid" ? `paid ${fmt(p.paid_at)}` : fmt(p.expires_at)}</td>
                    <td className="py-2 whitespace-nowrap">
                      {(p.status === "held" || p.status === "expired") && <button onClick={() => act(p.id, "confirm", `Confirm the €${p.deposit} transfer from ${p.name} has arrived? They get the confirmation email.`)} className="inline-flex items-center gap-1 text-emerald-700 hover:underline mr-3" data-testid={`admin-founder-confirm-${p.seat}`}><Check className="w-3 h-3" /> Paid</button>}
                      {(p.status === "held" || p.status === "expired") && <button onClick={() => act(p.id, "extend")} className="inline-flex items-center gap-1 text-[#3D4035]/70 hover:underline mr-3" data-testid={`admin-founder-extend-${p.seat}`}><Clock className="w-3 h-3" /> +{cfg.hold_hours}h</button>}
                      {(p.status === "held" || p.status === "paid") && <button onClick={() => act(p.id, "release", `Release seat ${p.seat}? It goes back on sale.`)} className="inline-flex items-center gap-1 text-[#3D4035]/70 hover:underline mr-3" data-testid={`admin-founder-release-${p.seat}`}><X className="w-3 h-3" /> Release</button>}
                      <button onClick={() => act(p.id, "delete", "Delete this pledge for good?")} className="inline-flex items-center gap-1 text-red-700/70 hover:underline" data-testid={`admin-founder-delete-${p.seat}`}><Trash2 className="w-3 h-3" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-6 pt-5 border-t border-stone-100 grid md:grid-cols-2 gap-5">
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-[#2E2A26]">LinkedIn line, as things stand</h3>
            <button type="button" onClick={() => copy(linkedin)} className="text-xs text-[#C85A38] hover:underline inline-flex items-center gap-1" data-testid="admin-founder-copy-linkedin"><Copy className="w-3 h-3" /> Copy</button>
          </div>
          <p className="text-xs text-[#2E2A26] whitespace-pre-line rounded-lg bg-[#FBF7F1] p-3" data-testid="admin-founder-linkedin">{linkedin}</p>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-[#2E2A26]">Card for the post</h3>
            <a href={`${API}/sojourns/founder/card.jpg`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#C85A38] hover:underline inline-flex items-center gap-1" data-testid="admin-founder-card-link">Open <ExternalLink className="w-3 h-3" /></a>
          </div>
          <img src={`${API}/sojourns/founder/card.jpg?t=${state.taken + state.held}`} alt="Founder Edition card" width="1200" height="630" className="w-full h-auto rounded-lg border border-stone-200" data-testid="admin-founder-card" />
          <p className="text-[11px] text-[#3D4035]/50 mt-1">Updates itself with the count. Live copy at {card_url.replace("https://", "")}</p>
        </div>
      </div>
    </div>
  );
}
